import { getApiBaseUrl, handleApiError } from '../../../core/config/api';
import { BaseUserService } from './baseService';
import type { PanelTeamTicket } from './types';

/** Sous-service extrait de userService.ts : tickets support internes équipe + reset rate-limit. */

export class UserTicketsService extends BaseUserService {
  async getPanelTeamTickets(): Promise<{
    success: boolean;
    data: PanelTeamTicket[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createPanelTeamTicket(body: {
    title: string;
    body: string;
    category?: string;
  }): Promise<{ success: boolean; message?: string; data: PanelTeamTicket; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updatePanelTeamTicketStatus(
    id: number,
    status: 'open' | 'in_progress' | 'closed'
  ): Promise<{ success: boolean; message?: string; data: PanelTeamTicket; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Réinitialise le blocage anti-brute-force (mémoire serveur) pour l'email du joueur.
   * Réservé aux administrateurs (API).
   */
  async clearAuthRateLimit(userId: number): Promise<{
    success: boolean;
    message?: string;
    cleared?: number;
    error?: string;
  }> {
    const response = await this.fetchWithAuth(
      `${getApiBaseUrl(true)}/users/clear-auth-rate-limit`,
      {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, error: data.error || `Erreur HTTP ${response.status}` };
    }
    return data;
  }
}
